import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import { memo } from 'react';

import type { SyntaxHighlightLine } from '@/components/ui/smoothdown/components/StaticCode/syntax-highlight.types';
import { uiStore } from '@/stores/ui.store';

import { ARTIFACT_MOCK_SOURCE } from './artifact-mock-source';
import {
  GLIDE_MS_PER_PX,
  useCopyHeight,
  useHighlightedMockSource,
} from './useArtifactCodePreview';

interface ArtifactCodePreviewProps {
  animate: boolean;
}

const CodeLines = memo(function CodeLines({
  tokenLines,
}: {
  tokenLines: SyntaxHighlightLine[] | null;
}) {
  if (!tokenLines) return <>{ARTIFACT_MOCK_SOURCE}</>;

  return (
    <>
      {tokenLines.map((line, lineIndex) => (
        <div key={lineIndex}>
          {line.length > 0
            ? line.map((token, tokenIndex) => (
                <span key={tokenIndex} style={{ color: token.color }}>
                  {token.content}
                </span>
              ))
            : ' '}
        </div>
      ))}
    </>
  );
});

export const ArtifactCodePreview = observer(function ArtifactCodePreview({
  animate,
}: ArtifactCodePreviewProps) {
  const theme = uiStore.theme === 'dark' ? 'github-dark' : 'github-light';
  const tokenLines = useHighlightedMockSource(theme);
  const { copyRef, copyHeight } = useCopyHeight(tokenLines !== null);
  const isGliding = animate && copyHeight > 0;

  return (
    <div className="pointer-events-none size-full select-none overflow-hidden opacity-60 [mask-image:linear-gradient(to_bottom,transparent_0,black_18%,black_82%,transparent_100%)]">
      <motion.pre
        className="text-[11px] font-mono leading-4 whitespace-pre"
        animate={isGliding ? { y: [0, -copyHeight] } : { y: 0 }}
        transition={
          isGliding
            ? {
                duration: (copyHeight * GLIDE_MS_PER_PX) / 1000,
                ease: 'linear',
                repeat: Infinity,
              }
            : { duration: 0 }
        }
      >
        <div ref={copyRef}>
          <CodeLines tokenLines={tokenLines} />
        </div>
        <CodeLines tokenLines={tokenLines} />
      </motion.pre>
    </div>
  );
});
